import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { useSelectedLocation } from './LocationContext';
import { reportTz, tzDateKey, tzDayRangeUtc, tzToday, tzShiftDays } from './tzUtils';

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom' },
];

/**
 * Tax Report — day-close-style card: taxes collected on sales for the
 * selected location(s), bucketed by each LOCATION's local day (never the
 * viewer's timezone — the Vegas-vs-Paris rule). Same range options as the
 * charts tab.
 */
const TaxReport = ({ locations = [], selectedLocationIds = [] }) => {
  const locationCtx = useSelectedLocation();
  const workingLocation = locationCtx?.selectedLocation || null;
  const [range, setRange] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [salesByLoc, setSalesByLoc] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const effectiveLocations = useMemo(() => {
    if (selectedLocationIds.length > 0) return locations.filter((l) => selectedLocationIds.includes(l.id));
    return workingLocation ? [workingLocation] : [];
  }, [selectedLocationIds, locations, workingLocation]);

  // Range in LOCATION-local days → UTC instants (per location tz)
  const rangeDates = useMemo(() => {
    const tz = reportTz(effectiveLocations[0]);
    const today = tzToday(tz);
    let startD = today, endD = today;
    if (range === 'yesterday') { startD = tzShiftDays(today, -1); endD = startD; }
    else if (range === 'last7days') startD = tzShiftDays(today, -6);
    else if (range === 'last30days') startD = tzShiftDays(today, -29);
    else if (range === 'thisMonth') startD = `${today.slice(0, 7)}-01`;
    else if (range === 'lastMonth') {
      const first = new Date(`${today.slice(0, 7)}-01T12:00:00Z`);
      first.setUTCMonth(first.getUTCMonth() - 1);
      startD = first.toISOString().slice(0, 10);
      endD = tzShiftDays(`${today.slice(0, 7)}-01`, -1);
    } else if (range === 'custom' && customStart) {
      startD = customStart;
      endD = customEnd || customStart;
    }
    return { startD, endD };
  }, [range, customStart, customEnd, effectiveLocations]);

  const load = async () => {
    const user = getAuth().currentUser;
    if (!user || effectiveLocations.length === 0) return;
    setLoading(true);
    setError('');
    try {
      const db = getFirestore();
      const results = await Promise.all(effectiveLocations.map(async (loc) => {
        const tz = reportTz(loc);
        const [startUtc] = tzDayRangeUtc(rangeDates.startD, tz);
        const [, endUtc] = tzDayRangeUtc(rangeDates.endD, tz);
        const q = query(
          collection(db, 'users', user.uid, 'sales'),
          where('locationId', '==', loc.id),
          where('orderDate', '>=', Timestamp.fromDate(startUtc)),
          where('orderDate', '<=', Timestamp.fromDate(endUtc)),
          orderBy('orderDate', 'asc')
        );
        const snap = await getDocs(q);
        return [loc.id, snap.docs.map((d) => ({ id: d.id, ...d.data() }))];
      }));
      setSalesByLoc(Object.fromEntries(results));
    } catch (e) {
      console.error('[TaxReport] load error:', e);
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDates.startD, rangeDates.endD, selectedLocationIds.join(','), workingLocation?.id]);

  // Metrics per location (bucketed by that location's local day)
  const metricsFor = (loc, sales) => {
    const tz = reportTz(loc);
    const byDay = {};
    let tax = 0, base = 0, orders = 0, canceled = 0, totalCharged = 0;
    for (const s of sales) {
      const status = (s.orderStatus || s.status || 'completed').toLowerCase();
      if (status === 'canceled' || status === 'cancelled') { canceled++; continue; }
      if (s.type === 'refund') continue; // mirror docs excluded (same rule as day close)
      const t = s.tax || 0;
      const sub = s.subtotal || 0;
      tax += t; base += sub; orders++;
      totalCharged += (s.total || 0);
      const key = tzDateKey(s.orderDate.toDate(), tz);
      if (!byDay[key]) byDay[key] = { date: key, tax: 0, base: 0, orders: 0 };
      byDay[key].tax += t; byDay[key].base += sub; byDay[key].orders++;
    }
    return {
      tax, base, orders, canceled, totalCharged,
      rate: base > 0 ? (tax / base) * 100 : 0,
      days: Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date)),
    };
  };

  const perLocation = useMemo(() => effectiveLocations.map((loc) => ({
    loc,
    m: metricsFor(loc, salesByLoc[loc.id] || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [effectiveLocations, salesByLoc]);

  const combined = useMemo(() => {
    // Combined buckets on the FIRST location's tz ruler (mixed-tz fleets read
    // consolidated numbers on one calendar — noted in the footer).
    const all = perLocation.flatMap(({ loc }) => (salesByLoc[loc.id] || []).map((s) => ({ ...s, _tz: reportTz(loc) })));
    const byDay = {};
    let tax = 0, base = 0, orders = 0, canceled = 0, totalCharged = 0;
    for (const s of all) {
      const status = (s.orderStatus || s.status || 'completed').toLowerCase();
      if (status === 'canceled' || status === 'cancelled') { canceled++; continue; }
      if (s.type === 'refund') continue;
      const t = s.tax || 0; const sub = s.subtotal || 0;
      tax += t; base += sub; orders++;
      const key = tzDateKey(s.orderDate.toDate(), s._tz);
      if (!byDay[key]) byDay[key] = { date: key, tax: 0, base: 0, orders: 0 };
      byDay[key].tax += t; byDay[key].base += sub; byDay[key].orders++;
    }
    return {
      tax, base, orders, canceled, totalCharged,
      rate: base > 0 ? (tax / base) * 100 : 0,
      days: Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date)),
    };
  }, [perLocation, salesByLoc]);

  if (effectiveLocations.length === 0) {
    return (
      <div className="card mb-4">
        <div className="card-body text-muted" style={{ fontSize: 14 }}>
          Select a working location (top bar) or pick locations above to see the tax report.
        </div>
      </div>
    );
  }

  const isMulti = effectiveLocations.length > 1;
  const locName = isMulti
    ? `${effectiveLocations.length} locations (combined)`
    : (effectiveLocations[0].companyName || effectiveLocations[0].locationName || 'Location');
  const rangeLabel = range === 'custom' ? `${rangeDates.startD} → ${rangeDates.endD}` : RANGE_OPTIONS.find((o) => o.value === range)?.label;
  const generatedAt = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const summaryRows = (m) => (
    <>
      <Row label="Taxable sales (subtotal)" value={money(m.base)} />
      <Row label="Tax collected" value={money(m.tax)} bold />
      <Row label="Total charged (incl. tax + tips)" value={money(m.totalCharged)} muted />
      <Row label="Effective tax rate" value={`${m.rate.toFixed(2)}%`} />
      <Row label="Orders with tax" value={m.orders} />
      {m.canceled > 0 && <Row label="Canceled orders (excluded)" value={m.canceled} muted />}
    </>
  );

  const daysTable = (m, title) => (
    <>
      <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#9ca3af', marginTop: 18, marginBottom: 6 }}>{title}</div>
      {m.days.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 13 }}>No sales in this period.</div>
      ) : (
        <table className="table table-sm" style={{ fontSize: 13, marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ borderTop: 'none' }}>Date</th>
              <th style={{ borderTop: 'none', textAlign: 'right' }}>Taxable sales</th>
              <th style={{ borderTop: 'none', textAlign: 'right' }}>Tax</th>
              <th style={{ borderTop: 'none', textAlign: 'right' }}>Orders</th>
            </tr>
          </thead>
          <tbody>
            {m.days.map((d) => (
              <tr key={d.date}>
                <td>{d.date}</td>
                <td style={{ textAlign: 'right' }}>{money(d.base)}</td>
                <td style={{ textAlign: 'right' }}>{money(d.tax)}</td>
                <td style={{ textAlign: 'right' }}>{d.orders}</td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 800, color: '#f97316' }}>TOTAL</td>
              <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{money(m.base)}</td>
              <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{money(m.tax)}</td>
              <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{m.orders}</td>
            </tr>
          </tbody>
        </table>
      )}
    </>
  );

  return (
    <div className="card mb-4 tax-report" style={{ border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
      <style>{`@media print { body * { visibility: hidden; } .tax-report, .tax-report * { visibility: visible; } .tax-report { position: absolute; left: 0; top: 0; width: 100%; border: none; } .tax-report-no-print { display: none !important; } }`}</style>
      <div style={{ background: '#1f2937', color: '#fff', padding: '18px 24px' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#f97316', fontWeight: 800 }}>Tax Report</div>
            <h4 className="mb-0 font-weight-bold" style={{ color: '#fff' }}>{locName}</h4>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>{rangeLabel}</div>
          </div>
          <div className="d-flex align-items-center tax-report-no-print" style={{ gap: 8 }}>
            <select className="form-control form-control-sm" style={{ width: 150 }} value={range} onChange={(e) => setRange(e.target.value)}>
              {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {range === 'custom' && (
              <>
                <input type="date" className="form-control form-control-sm" style={{ width: 140 }} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                <input type="date" className="form-control form-control-sm" style={{ width: 140 }} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </>
            )}
            <button className="btn btn-sm btn-outline-light" onClick={load} disabled={loading}>
              <i className="fas fa-rotate"></i>
            </button>
            <button className="btn btn-sm btn-warning" style={{ background: '#f97316', borderColor: '#f97316', color: '#fff' }} onClick={() => window.print()}>
              <i className="fas fa-print mr-1"></i>Print
            </button>
          </div>
        </div>
      </div>

      <div className="card-body" style={{ padding: '8px 24px 20px' }}>
        {error && <div className="alert alert-danger mt-3">{error}</div>}
        {loading ? (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#f97316' }} /></div>
        ) : isMulti ? (
          <>
            {/* Per-location columns + combined TOTAL (same pattern as the day close) */}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-sm" style={{ fontSize: 13, marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={{ borderTop: 'none' }}></th>
                    {perLocation.map(({ loc }) => (
                      <th key={loc.id} style={{ borderTop: 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>{loc.companyName || loc.locationName}</th>
                    ))}
                    <th style={{ borderTop: 'none', textAlign: 'right', color: '#f97316', fontWeight: 800 }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td style={{ color: '#6b7280' }}>Total charged</td>
                    {perLocation.map(({ loc, m }) => <td key={loc.id} style={{ textAlign: 'right' }}>{money(m.totalCharged)}</td>)}
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{money(combined.totalCharged)}</td></tr>
                  <tr><td style={{ color: '#6b7280' }}>Taxable sales</td>
                    {perLocation.map(({ loc, m }) => <td key={loc.id} style={{ textAlign: 'right' }}>{money(m.base)}</td>)}
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{money(combined.base)}</td></tr>
                  <tr><td style={{ fontWeight: 800 }}>Tax collected</td>
                    {perLocation.map(({ loc, m }) => <td key={loc.id} style={{ textAlign: 'right', fontWeight: 800 }}>{money(m.tax)}</td>)}
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{money(combined.tax)}</td></tr>
                  <tr><td style={{ color: '#6b7280' }}>Effective rate</td>
                    {perLocation.map(({ loc, m }) => <td key={loc.id} style={{ textAlign: 'right' }}>{m.rate.toFixed(2)}%</td>)}
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{combined.rate.toFixed(2)}%</td></tr>
                  <tr><td style={{ color: '#6b7280' }}>Orders</td>
                    {perLocation.map(({ loc, m }) => <td key={loc.id} style={{ textAlign: 'right' }}>{m.orders}</td>)}
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#f97316' }}>{combined.orders}</td></tr>
                </tbody>
              </table>
            </div>
            {daysTable(combined, 'By Day (all selected locations)')}
          </>
        ) : (
          <>
            {summaryRows(combined)}
            {daysTable(combined, 'By Day')}
          </>
        )}
        <div className="text-center mt-3" style={{ fontSize: 11, color: '#9ca3af' }}>
          Generated {generatedAt} · FodyFee · {locName} · days bucketed by each location's timezone
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold, muted }) => (
  <div className="d-flex justify-content-between" style={{ padding: '6px 0', borderBottom: '1px dashed #e5e7eb', fontSize: 14 }}>
    <span style={{ color: muted ? '#6b7280' : '#1f2937', fontWeight: bold ? 800 : 500 }}>{label}</span>
    <span style={{ color: muted ? '#6b7280' : '#111827', fontWeight: bold ? 800 : 600 }}>{value}</span>
  </div>
);

export default TaxReport;
